import { useState } from 'react'
import { useShareLinks, useCreateShareLink, useRevokeShareLink } from '../../hooks/useSharing'

const SECTION_LABELS: Record<string, string> = {
  executive: 'Executive Cockpit',
  revenue: 'Revenue Ops',
  margin: 'Margin Control',
  billing: 'Billing & AR',
  recurring: 'Recurring Revenue',
}

const ALL_SECTIONS = Object.keys(SECTION_LABELS)

interface Props {
  onClose: () => void
}

export function ShareModal({ onClose }: Props) {
  const { data: links, isLoading } = useShareLinks()
  const createLink = useCreateShareLink()
  const revokeLink = useRevokeShareLink()

  const [selectedSections, setSelectedSections] = useState<string[]>(['executive'])
  const [expiresInDays, setExpiresInDays] = useState<7 | 30 | 90>(30)
  const [newUrl, setNewUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  function toggleSection(section: string) {
    setSelectedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    )
  }

  function handleCreate() {
    if (selectedSections.length === 0) return
    createLink.mutate(
      { sections: selectedSections, expires_in_days: expiresInDays },
      { onSuccess: (data) => setNewUrl(data.url) }
    )
  }

  function handleCopy(url: string) {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleRevoke(linkId: string) {
    if (!confirm('Revocare questo link? Chiunque lo abbia non potrà più accedere.')) return
    revokeLink.mutate(linkId)
  }

  const activeLinks = links?.filter((l) => !l.is_expired) ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="w-full max-w-lg rounded-2xl border shadow-2xl flex flex-col max-h-[90vh]"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--km-border)' }}>
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--km-text-primary)' }}>
              Condividi dashboard
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--km-text-muted)' }}>
              Link read-only per CDA, investitori e advisor
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-white/10 transition-colors"
            style={{ color: 'var(--km-text-muted)' }}
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* Link appena creato */}
          {newUrl && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 space-y-2">
              <p className="text-xs font-semibold text-emerald-400">Link generato con successo!</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={newUrl}
                  className="flex-1 rounded-lg border px-3 py-1.5 text-xs font-mono truncate"
                  style={{ background: 'var(--km-bg)', borderColor: 'var(--km-border)', color: 'var(--km-text-secondary)' }}
                />
                <button
                  onClick={() => handleCopy(newUrl)}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
                  style={{ background: copied ? 'var(--km-primary)' : 'var(--km-surface-2)', color: 'var(--km-text-primary)' }}
                >
                  {copied ? 'Copiato!' : 'Copia'}
                </button>
              </div>
            </div>
          )}

          {/* Form nuovo link */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium" style={{ color: 'var(--km-text-secondary)' }}>
              Nuovo link
            </h3>

            {/* Sezioni */}
            <div>
              <p className="text-xs mb-2" style={{ color: 'var(--km-text-muted)' }}>Sezioni da condividere</p>
              <div className="flex flex-wrap gap-2">
                {ALL_SECTIONS.map((s) => {
                  const active = selectedSections.includes(s)
                  return (
                    <button
                      key={s}
                      onClick={() => toggleSection(s)}
                      className="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors border"
                      style={{
                        background: active ? 'rgba(99,102,241,0.2)' : 'var(--km-surface-2)',
                        borderColor: active ? 'var(--km-primary)' : 'var(--km-border)',
                        color: active ? 'var(--km-primary)' : 'var(--km-text-secondary)',
                      }}
                    >
                      {SECTION_LABELS[s]}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Scadenza */}
            <div>
              <p className="text-xs mb-2" style={{ color: 'var(--km-text-muted)' }}>Scadenza</p>
              <div className="flex gap-2">
                {([7, 30, 90] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setExpiresInDays(d)}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors border"
                    style={{
                      background: expiresInDays === d ? 'rgba(99,102,241,0.2)' : 'var(--km-surface-2)',
                      borderColor: expiresInDays === d ? 'var(--km-primary)' : 'var(--km-border)',
                      color: expiresInDays === d ? 'var(--km-primary)' : 'var(--km-text-secondary)',
                    }}
                  >
                    {d} giorni
                  </button>
                ))}
              </div>
            </div>

            {createLink.error && (
              <p className="text-xs text-red-400">{createLink.error.message}</p>
            )}

            <button
              onClick={handleCreate}
              disabled={createLink.isPending || selectedSections.length === 0}
              className="w-full rounded-lg py-2 text-sm font-medium text-white transition-opacity disabled:opacity-50 hover:opacity-90"
              style={{ background: 'var(--km-gradient-brand)' }}
            >
              {createLink.isPending ? 'Generazione...' : 'Genera link'}
            </button>
          </div>

          {/* Link attivi */}
          {isLoading ? (
            <p className="text-xs" style={{ color: 'var(--km-text-muted)' }}>Caricamento link...</p>
          ) : activeLinks.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-medium" style={{ color: 'var(--km-text-secondary)' }}>
                Link attivi ({activeLinks.length})
              </h3>
              <div className="space-y-2">
                {activeLinks.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center justify-between rounded-lg border px-3 py-2 gap-3"
                    style={{ borderColor: 'var(--km-border)', background: 'var(--km-surface-2)' }}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate" style={{ color: 'var(--km-text-primary)' }}>
                        {link.sections.map((s) => SECTION_LABELS[s] ?? s).join(', ')}
                      </p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--km-text-muted)' }}>
                        Scade: {new Date(link.expires_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRevoke(link.id)}
                      disabled={revokeLink.isPending}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors flex-shrink-0 disabled:opacity-50"
                    >
                      Revoca
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
